import { useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm, type DefaultValues, type Path, type PathValue } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { ZodSchema } from "zod";
import { slugify } from "@ckgo2/shared/utils";
import { OPTIMISTIC_LOCK_HEADER } from "@ckgo2/shared";
import { api, ApiError, type ApiResponse } from "@/api/client";
import { humanizeFieldMessage } from "@/lib/errorMessages";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface UseEditPageOptions<TForm extends Record<string, any>, TData> {
  /** Query key for single item, e.g. "activity" */
  resourceKey: string;
  /** Query key for list invalidation, e.g. "activities" */
  listKey: string;
  /** API base path, e.g. "/api/admin/activities" */
  apiPath: string;
  /** Zod schema for form validation (optional — omit for no validation) */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  schema?: ZodSchema<any>;
  /** Default form values */
  defaultValues: TForm;
  /** Where to navigate after save, e.g. "/activities" */
  navigateTo: string;
  /** Field to watch for auto-slug generation (default: "title") */
  slugSourceField?: keyof TForm & string;
  /** Transform loaded data before resetting the form */
  transformOnLoad?: (data: TData) => TForm;
  /** Transform form data before sending to API */
  transformOnSave?: (data: TForm) => unknown;
  /** Extra query dependencies for reset effect (e.g., wait for dropdown data) */
  resetDeps?: unknown[];
  /** Stay on page after save instead of navigating (like trips editor) */
  stayOnSave?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useEditPage<TForm extends Record<string, any>, TData = TForm>(
  options: UseEditPageOptions<TForm, TData>,
) {
  const {
    resourceKey,
    listKey,
    apiPath,
    schema,
    defaultValues,
    navigateTo,
    slugSourceField = "title",
    transformOnLoad,
    transformOnSave,
    resetDeps = [],
    stayOnSave = false,
  } = options;

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNew = id === "new";

  // Optimistic-lock state (Block 6.1 Řez 1b): the `updatedAt` we loaded into the form (sent
  // back so the server can detect a concurrent save), and a one-shot flag set when the user
  // chose to overwrite a conflicting change.
  const baseVersionRef = useRef<string | undefined>(undefined);
  const forceOverwriteRef = useRef(false);

  const form = useForm<TForm>({
    ...(schema ? { resolver: zodResolver(schema) } : {}),
    defaultValues: defaultValues as DefaultValues<TForm>,
  });

  const { reset, watch, setValue } = form;

  // Fetch item if editing
  const {
    data: itemData,
    isLoading,
    error,
    isError,
  } = useQuery({
    queryKey: [resourceKey, id],
    queryFn: () => api.get<ApiResponse<TData>>(`${apiPath}/${id}`),
    enabled: !isNew,
  });

  // Reset form when data loads
  useEffect(() => {
    if (itemData?.data) {
      const formData = transformOnLoad
        ? transformOnLoad(itemData.data)
        : (itemData.data as unknown as TForm);
      reset(formData as DefaultValues<TForm>);
      // Capture the version we just loaded — a later save echoes it as the optimistic-lock base.
      baseVersionRef.current = (itemData.data as { updatedAt?: string }).updatedAt;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemData, reset, ...resetDeps]);

  // Auto-generate slug from source field
  const sourceValue = watch(slugSourceField as Path<TForm>);
  useEffect(() => {
    if (isNew && sourceValue && typeof sourceValue === "string") {
      setValue("slug" as Path<TForm>, slugify(sourceValue) as PathValue<TForm, Path<TForm>>, {
        shouldDirty: false,
      });
    }
  }, [sourceValue, isNew, setValue]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (data: TForm) => {
      const apiData = transformOnSave ? transformOnSave(data) : data;
      if (isNew) {
        return api.post<ApiResponse<TData>>(apiPath, apiData);
      }
      // Echo the loaded version so the server can reject a stale overwrite (409). When the user
      // chose "overwrite" after a conflict, drop the header to force it. The flag is cleared in
      // onSuccess, not here — so if a forced save fails (transient 5xx/network) the user's
      // overwrite choice survives the retry instead of bouncing back to a 409.
      const headers =
        baseVersionRef.current && !forceOverwriteRef.current
          ? { [OPTIMISTIC_LOCK_HEADER]: baseVersionRef.current }
          : undefined;
      return api.put<ApiResponse<TData>>(`${apiPath}/${id}`, apiData, headers);
    },
    onError: (error: Error) => {
      // Set server-side validation errors on form fields so they show inline
      if (error instanceof ApiError && error.fieldErrors) {
        for (const [field, messages] of Object.entries(error.fieldErrors)) {
          if (messages.length > 0) {
            form.setError(field as Path<TForm>, {
              type: "server",
              message: humanizeFieldMessage(messages[0]),
            });
          }
        }
      }
    },
    onSuccess: (response, variables) => {
      // The save landed, so a pending "force overwrite" choice has been honoured — clear it.
      forceOverwriteRef.current = false;
      // Keep the optimistic-lock base in sync with our OWN write: the PUT returns the row with its
      // freshly-bumped updatedAt, so a rapid second save (stayOnSave editors) doesn't fire a
      // spurious self-conflict 409 while the background refetch is still in flight.
      const responseData = (response as ApiResponse<TData & { id?: string; updatedAt?: string }>)
        .data;
      if (responseData?.updatedAt) baseVersionRef.current = responseData.updatedAt;
      if (stayOnSave) {
        // Stay on page: only refetch current item, list will refresh on navigation
        queryClient.invalidateQueries({ queryKey: [resourceKey, id] });
        if (isNew && responseData?.id) {
          navigate(`${navigateTo}/${responseData.id}`);
        }
        reset(variables as DefaultValues<TForm>);
      } else {
        // Navigating away: remove cache so list page fetches fresh data
        queryClient.removeQueries({ queryKey: [listKey] });
        queryClient.removeQueries({ queryKey: [resourceKey, id] });
        navigate(navigateTo);
      }
    },
  });

  const onSubmit = async (data: TForm) => {
    try {
      await saveMutation.mutateAsync(data);
    } catch (err) {
      // Concurrent-edit conflict (Block 6.1 Řez 1b): another editor saved since this form
      // loaded. Offer to overwrite (force) or keep editing; on overwrite, retry once without
      // the version header so the server skips the check.
      if (err instanceof ApiError && err.isConflict) {
        const when = err.changedAt ? new Date(err.changedAt).toLocaleString() : "";
        const overwrite = window.confirm(
          `This record was changed by someone else${when ? ` at ${when}` : ""} since you opened it.\n\n` +
            `OK = overwrite their changes with yours\n` +
            `Cancel = keep your edits (reload the page to see the latest first).`,
        );
        if (overwrite) {
          forceOverwriteRef.current = true;
          await saveMutation.mutateAsync(data);
        }
        return;
      }
      throw err;
    }
  };

  return {
    id,
    isNew,
    isLoading: !isNew && isLoading,
    isError: !isNew && isError,
    error,
    form,
    saveMutation,
    onSubmit,
    navigate,
    queryClient,
    itemData: itemData?.data ?? null,
  };
}
